import React from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, Radar, Legend } from 'recharts';

const ORANGE = '#f97316';

/* ---------- Monthly performance area chart ---------- */
export function PerformanceChart({ data }) {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 12, left: -14, bottom: 0 }}>
          <defs>
            <linearGradient id="wrGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ORANGE} stopOpacity={0.55} />
              <stop offset="100%" stopColor={ORANGE} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#2e2822" vertical={false} />
          <XAxis dataKey="month" tick={{ fill: '#8a827a', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#2e2822' }} interval={'preserveStartEnd'} />
          <YAxis domain={[0, 100]} tick={{ fill: '#8a827a', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
          <Tooltip contentStyle={{ background: '#1b1712', border: '1px solid #2e2822', borderRadius: 10, color: '#fff' }} labelStyle={{ color: ORANGE }} formatter={(v, n) => n === 'winRate' ? [`${v}%`, 'Win Rate'] : [v, n]} />
          <Area type="monotone" dataKey="winRate" stroke={ORANGE} strokeWidth={2.5} fill="url(#wrGrad)" dot={false} activeDot={{ r: 4, fill: ORANGE }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------- Team comparison radar ---------- */
export function FormRadar({ data, home = 'Home', away = 'Away' }) {
  return (
    <div className="h-[260px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke="#2e2822" />
          <PolarAngleAxis dataKey="stat" tick={{ fill: '#a89f95', fontSize: 11 }} />
          <Radar name={home} dataKey="home" stroke={ORANGE} fill={ORANGE} fillOpacity={0.35} strokeWidth={2} />
          <Radar name={away} dataKey="away" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.18} strokeWidth={2} />
          <Legend wrapperStyle={{ fontSize: 12, color: '#a89f95' }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------- Reusable pitch background (vertical) ---------- */
function PitchBg({ children, viewBox = '0 0 200 300' }) {
  return (
    <svg viewBox={viewBox} className="h-full w-full">
      <rect x="0" y="0" width="200" height="300" fill="#12261a" />
      <g stroke="#2f5f43" strokeWidth="1.4" fill="none">
        <rect x="8" y="8" width="184" height="284" />
        <line x1="8" y1="150" x2="192" y2="150" />
        <circle cx="100" cy="150" r="26" />
        <circle cx="100" cy="150" r="2" fill="#2f5f43" />
        <rect x="55" y="8" width="90" height="42" />
        <rect x="78" y="8" width="44" height="16" />
        <rect x="55" y="250" width="90" height="42" />
        <rect x="78" y="276" width="44" height="16" />
      </g>
      {children}
    </svg>
  );
}

/* ---------- Heatmap over pitch ---------- */
export function HeatmapPitch() {
  const blobs = [
    { x: 100, y: 90, r: 46, o: 0.85 },
    { x: 70, y: 130, r: 40, o: 0.6 },
    { x: 130, y: 120, r: 38, o: 0.55 },
    { x: 100, y: 190, r: 34, o: 0.4 },
    { x: 60, y: 60, r: 26, o: 0.5 },
    { x: 140, y: 70, r: 24, o: 0.45 },
  ];
  return (
    <PitchBg>
      <defs>
        <radialGradient id="heat" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#f97316" stopOpacity="0.9" />
          <stop offset="45%" stopColor="#f59e0b" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
        </radialGradient>
      </defs>
      {blobs.map((b, i) => (
        <circle key={i} cx={b.x} cy={b.y} r={b.r} fill="url(#heat)" opacity={b.o} />
      ))}
    </PitchBg>
  );
}

/* ---------- Shot map ---------- */
export function ShotMap() {
  const shots = [
    { x: 100, y: 40, goal: true, s: 5 },
    { x: 82, y: 52, goal: false, s: 3 },
    { x: 118, y: 55, goal: true, s: 4 },
    { x: 95, y: 70, goal: false, s: 3 },
    { x: 108, y: 30, goal: true, s: 6 },
    { x: 70, y: 66, goal: false, s: 2.5 },
    { x: 130, y: 78, goal: false, s: 3 },
    { x: 100, y: 24, goal: true, s: 5 },
  ];
  return (
    <PitchBg viewBox="0 0 200 150">
      <g stroke="#2f5f43" strokeWidth="1.4" fill="none">
        <rect x="8" y="8" width="184" height="134" />
        <rect x="55" y="8" width="90" height="42" />
        <rect x="78" y="8" width="44" height="16" />
      </g>
      {shots.map((sh, i) => (
        <g key={i}>
          <circle cx={sh.x} cy={sh.y} r={sh.s} fill={sh.goal ? '#f97316' : 'none'} stroke={sh.goal ? '#fff' : '#38bdf8'} strokeWidth={sh.goal ? 1 : 1.4} opacity={0.95} />
        </g>
      ))}
    </PitchBg>
  );
}

/* ---------- Passing network ---------- */
export function PassingNetwork() {
  const nodes = [
    { id: 'gk', x: 100, y: 260, r: 6 },
    { id: 'lb', x: 45, y: 210, r: 6 }, { id: 'cb1', x: 80, y: 225, r: 7 }, { id: 'cb2', x: 120, y: 225, r: 7 }, { id: 'rb', x: 155, y: 210, r: 6 },
    { id: 'cm1', x: 70, y: 160, r: 8 }, { id: 'cm2', x: 130, y: 160, r: 8 }, { id: 'am', x: 100, y: 120, r: 9 },
    { id: 'lw', x: 55, y: 80, r: 7 }, { id: 'rw', x: 145, y: 80, r: 7 }, { id: 'st', x: 100, y: 55, r: 9 },
  ];
  const by = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const links = [['gk','cb1'],['gk','cb2'],['cb1','lb'],['cb2','rb'],['cb1','cm1'],['cb2','cm2'],['cm1','cm2'],['cm1','am'],['cm2','am'],['am','st'],['am','lw'],['am','rw'],['lw','st'],['rw','st'],['lb','cm1'],['rb','cm2']];
  return (
    <PitchBg>
      <g stroke="#f97316" strokeOpacity="0.4" strokeWidth="1.2">
        {links.map(([a, b], i) => (
          <line key={i} x1={by[a].x} y1={by[a].y} x2={by[b].x} y2={by[b].y} />
        ))}
      </g>
      {nodes.map((n) => (
        <g key={n.id}>
          <circle cx={n.x} cy={n.y} r={n.r} fill="#f97316" stroke="#fff" strokeWidth="1" />
        </g>
      ))}
    </PitchBg>
  );
}
